import React from 'react';
import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { getBookingStatusColor } from '../helpers/getBookingStatusColor';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';
import { getTransactionSourceLabel } from '../helpers/getTransactionSourceLabel';

const BookingFeeReportTable = ({ items, summary }) => {
    const { t } = useTranslation();
    const sort = useSort(items || [], 'bookingDate', 'desc');

    return (
        <Box sx={{ width: '100%' }}>
            <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }}>
                    <TableHead>
                        <TableRow>
                            <SortableHeaderCell field="bookingId" sort={sort}>
                                {t('reports.columns.bookingId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingStatus" sort={sort}>
                                {t('reports.columns.bookingStatus')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="showTitle" sort={sort}>
                                {t('reports.columns.show', 'Show')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="customers" sort={sort}>
                                {t('reports.columns.customers')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingDate" sort={sort}>
                                {t('reports.manifest.columns.createdAt', 'Created at')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="source" sort={sort}>
                                {t('reports.columns.source')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingFees" sort={sort} align="right">
                                {t('reports.columns.bookingFees', 'Booking fees')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="refundedFees" sort={sort} align="right">
                                {t('reports.columns.refundedFees', 'Refunded fees')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="netFees" sort={sort} align="right">
                                {t('reports.columns.netFees', 'Net fees')}
                            </SortableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sort.sortedItems.map((item) => (
                            <TableRow key={item.bookingId}>
                                <TableCell>{item.bookingId}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`bookingstatus.${item.bookingStatus}`)}
                                        color={getBookingStatusColor(item.bookingStatus)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{item.showTitle}</TableCell>
                                <TableCell>
                                    {item.customers.split(', ').map((line) => (
                                        <div key={line}>{line}</div>
                                    ))}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption">
                                        {item.bookingDate
                                            ? formatDateTimeInVenueTz(item.bookingDate)
                                            : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {getTransactionSourceLabel(
                                        {
                                            transactionSource: item.source,
                                            transactionAgentName: item.agentName,
                                            transactionCommissionGroups: item.commissionGroups,
                                        },
                                        t,
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.bookingFees)}
                                </TableCell>
                                <TableCell align="right">
                                    {item.refundedFees > 0 ? (
                                        <Typography
                                            component="span"
                                            sx={{ color: 'error.main', fontWeight: 600 }}
                                        >
                                            {formatCentsToUsd(item.refundedFees)}
                                        </Typography>
                                    ) : (
                                        formatCentsToUsd(0)
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(item.netFees)}</strong>
                                </TableCell>
                            </TableRow>
                        ))}
                        {summary && (
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell colSpan={6}>
                                    <strong>
                                        {t('reports.summary.total')} -{' '}
                                        {t('reports.summary.bookings', {
                                            count: summary.totalBookings,
                                        })}
                                    </strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(summary.totalFees)}</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(summary.totalRefundedFees)}</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(summary.totalNetFees)}</strong>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default BookingFeeReportTable;
